/**
 * Timezone utilities for Czech timezone (Europe/Prague)
 *
 * This ensures the entire app operates in Czech local time:
 * - Notifications fire at correct local times
 * - "Today" means today in Prague, not UTC
 * - Streaks break at Czech midnight, not UTC midnight
 * - Handles automatic DST (summer/winter time) transitions
 */

import { toZonedTime, formatInTimeZone } from 'date-fns-tz'
import { subDays } from 'date-fns'

/**
 * Czech timezone identifier (handles CET/CEST automatically)
 */
export const PRAGUE_TZ = 'Europe/Prague'

/**
 * Get current date/time in Czech timezone
 *
 * @returns Date object representing "now" in Prague
 *
 * @example
 * const now = getNowInPrague()
 * // Even if server is UTC, this returns Prague local time
 */
export function getNowInPrague(): Date {
  return toZonedTime(new Date(), PRAGUE_TZ)
}

/**
 * Get today's date in Czech timezone as ISO string (YYYY-MM-DD)
 *
 * @returns ISO date string for "today" in Prague
 *
 * @example
 * // At 00:30 Prague time on Jan 15 (23:30 UTC on Jan 14):
 * getTodayInPrague() // Returns "2025-01-15" ✅
 * new Date().toISOString().split('T')[0] // Returns "2025-01-14" ❌
 */
export function getTodayInPrague(): string {
  return formatInTimeZone(new Date(), PRAGUE_TZ, 'yyyy-MM-dd')
}

/**
 * Get current time in Czech timezone as HH:MM:SS string
 *
 * @returns Time string in HH:MM:SS format (24-hour)
 *
 * @example
 * // At 13:50:22 Prague time (12:50:22 UTC in winter):
 * getTimestampInPrague() // Returns "13:50:22" ✅
 * new Date().toISOString().split('T')[1].split('.')[0] // Returns "12:50:22" ❌
 */
export function getTimestampInPrague(): string {
  return formatInTimeZone(new Date(), PRAGUE_TZ, 'HH:mm:ss')
}

/**
 * Format a date in Czech timezone to ISO date string (YYYY-MM-DD)
 *
 * @param date - Date to format
 * @returns ISO date string in Prague timezone
 */
export function formatDateInPrague(date: Date): string {
  return formatInTimeZone(date, PRAGUE_TZ, 'yyyy-MM-dd')
}

/**
 * Format a date in Czech timezone to time string (HH:MM:SS)
 *
 * @param date - Date to format
 * @returns Time string in HH:MM:SS format
 */
export function formatTimeInPrague(date: Date): string {
  return formatInTimeZone(date, PRAGUE_TZ, 'HH:mm:ss')
}

/**
 * Get today's date in any timezone as ISO string (YYYY-MM-DD)
 *
 * @param timezone - IANA timezone identifier (e.g., "America/New_York", "Europe/Prague")
 * @returns ISO date string for "today" in the specified timezone
 *
 * @example
 * getTodayInTimezone("America/New_York") // Returns "2025-01-15"
 * getTodayInTimezone("Asia/Tokyo") // Returns "2025-01-16" (if a day ahead)
 */
export function getTodayInTimezone(timezone: string): string {
  return formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd')
}

/**
 * Get yesterday's date in any timezone as ISO string (YYYY-MM-DD)
 *
 * @param timezone - IANA timezone identifier
 * @returns ISO date string for "yesterday" in the specified timezone
 *
 * @example
 * getYesterdayInTimezone("Europe/Prague") // Returns "2025-01-14" (if today is Jan 15)
 */
export function getYesterdayInTimezone(timezone: string): string {
  return formatInTimeZone(subDays(new Date(), 1), timezone, 'yyyy-MM-dd')
}

/**
 * Get current date/time in any timezone
 *
 * @param timezone - IANA timezone identifier
 * @returns Date object representing "now" in the specified timezone
 *
 * @example
 * getNowInTimezone("America/Los_Angeles")
 * // Returns Date object for current Pacific time
 */
export function getNowInTimezone(timezone: string): Date {
  return toZonedTime(new Date(), timezone)
}

/**
 * Format a date in any timezone to ISO date string (YYYY-MM-DD)
 *
 * @param date - Date to format
 * @param timezone - IANA timezone identifier
 * @returns ISO date string in the specified timezone
 *
 * @example
 * formatDateInTimezone(new Date(), "Europe/Prague") // Returns "2025-01-15"
 */
export function formatDateInTimezone(date: Date, timezone: string): string {
  return formatInTimeZone(date, timezone, 'yyyy-MM-dd')
}
